import { useMemo } from 'react';

import { filterRows, type MarketingPerformanceFilters } from './filters';
import {
  computeFunnelMatrix,
  emptyStages,
  funnelCohort,
  orderBrands,
  type FunnelMatrix,
  type FunnelStageValues,
} from './funnel';
import type { YougovData, YougovMonthlyRow } from './yougovData.schema';

export interface MarketingPerformanceData {
  filteredRows: YougovMonthlyRow[];
  orderedBrands: string[];
  targetBrand: string;
  kpis: FunnelStageValues;
  funnel: FunnelMatrix;
}

export function useMarketingPerformanceData(
  data: YougovData,
  filters: MarketingPerformanceFilters,
): MarketingPerformanceData {
  return useMemo(() => {
    const filteredRows = filterRows(data.monthly, filters);
    // `orderedBrands` stays every distinct brand — ExtendedMetrics and MetricTrends
    // should still show a brand with extended-metric data but no funnel data. Only
    // the funnel matrix narrows to the cohort that actually has a scored funnel metric.
    const orderedBrands = orderBrands(data.availableBrands, data.targetBrand);
    const cohort = funnelCohort(filteredRows, data.availableBrands, data.targetBrand);
    const funnel = computeFunnelMatrix(filteredRows, data.targetBrand, cohort);

    return {
      filteredRows,
      orderedBrands,
      targetBrand: data.targetBrand,
      kpis: funnel.values[data.targetBrand] ?? emptyStages(),
      funnel,
    };
  }, [data, filters]);
}
